import { Tooltip, Typography, TypographyProps } from '@mui/material';
import { Configurable, RaRecord, usePreference, useRecordContext, useTranslate } from 'react-admin';
import { get, isNil } from 'lodash';
import { DateTime } from 'luxon';
import { formatInTimeZone } from 'date-fns-tz';
import {
  SERVER_TIMEZONE,
  supportedDateFormats,
  supportedTimeFormats,
  supportedTimezoneFormats,
  validTimezones,
} from './utils';
import TzDatePreferencesEditor from './TzDatePreferencesEditor';
import { ReactElement } from 'react';

type TzDateFieldImplProps = TypographyProps & {
  emptyText?: string;
  ignoreTimezoneInfo?: boolean;
  serverTimezone?: string;
  showDate?: boolean;
  showTime?: boolean;
  source?: string;
};

const zeroDates = ['0000-00-00', '0001-01-01', '1970-01-01'];
const timeSeparators = [' ', 'T'];
const zeroTimes = ['00:00:00'];
const millis = ['', '.000'];
const timezoneSpecifiers = ['', 'Z', '+00:00'];

/**
 * List of date strings that should be treated as empty values.
 * Examples of generated empty date formats include:
 * - "0000-00-00 00:00:00"
 * - "0001-01-01T00:00:00.000Z"
 * - "1970-01-01 00:00:00+00:00"
 */
// Disabling eslint for this block as in this particular case our rules make
// this much less readable than we'd normally hope
/* eslint-disable */
const emptyDateFormats = zeroDates
  .flatMap((date) => timeSeparators
    .flatMap((sep) => zeroTimes
      .flatMap((time) => millis
        .flatMap((ms) => timezoneSpecifiers
          .map((tz) => `${date}${sep}${time}${ms}${tz}`)))));
/* eslint-enable */

function TzDateFieldImpl<Record extends RaRecord>({
  emptyText,
  ignoreTimezoneInfo = false,
  serverTimezone = SERVER_TIMEZONE,
  showDate = true,
  showTime = false,
  source,
  ...typographyProps
}: TzDateFieldImplProps): ReactElement {
  const [userTimezone] = usePreference('timezone', validTimezones[0].id);
  const [dateFormat] = usePreference('format.date', supportedDateFormats[0].id);
  const [timeFormat] = usePreference('format.time', supportedTimeFormats[0].id);
  const [timezoneFormat] = usePreference('format.timezone', supportedTimezoneFormats[0].id);
  const record = useRecordContext<Record>();
  const translate = useTranslate();

  const emptyValue = (
    <Typography component="span" variant="body2" {...typographyProps}>
      {emptyText ? translate(emptyText, { _: emptyText }) : null}
    </Typography>
  );

  if (isNil(source) || source === '') {
    return emptyValue;
  }

  const serverTime = get(record, source);

  if (isNil(serverTime) || emptyDateFormats.includes(serverTime)) {
    return emptyValue;
  }

  const time = DateTime
    // Parse the server time, with a default timezone of `serverTimezone` if
    // the string doesn't include timezone info
    .fromISO(serverTime, { zone: serverTimezone })
    // Convert to the user's timezone, keeping the local time the same if
    // `ignoreTimezoneInfo` is true (e.g. if TZ info doesn't matter)
    .setZone(userTimezone, { keepLocalTime: ignoreTimezoneInfo });

  const formatString = [showDate ? dateFormat : null, showTime ? timeFormat : null]
    .filter((f) => f !== null)
    .join(' ');

  const formattedTime = formatInTimeZone(time.toJSDate(), userTimezone, formatString);

  const unicodeNonBreakingSpace = '\u00a0';
  const timezoneString =
    timezoneFormat === '' ? '' : `${unicodeNonBreakingSpace}${time.toFormat(timezoneFormat)}`;

  return (
    <Typography component="span" variant="body2" {...typographyProps}>
      <Tooltip
        title={
          formatInTimeZone(time.toJSDate(), userTimezone, `${dateFormat} ${timeFormat}`) +
          (!ignoreTimezoneInfo ? timezoneString : '')
        }
      >
        <span>
          {formattedTime}
          {showTime && !ignoreTimezoneInfo ? timezoneString : null}
        </span>
      </Tooltip>
    </Typography>
  );
}

export type TzDateFieldProps = TzDateFieldImplProps & {
  preferenceKey?: string;
  label?: string;
};

export default function TzDateField<Record extends RaRecord>({
  preferenceKey = 'tzDatePreferences',
  ...rest
}: TzDateFieldProps): ReactElement {
  return (
    <Configurable
      editor={<TzDatePreferencesEditor showDate={rest.showDate} showTime={rest.showTime} />}
      preferenceKey={preferenceKey}
    >
      <TzDateFieldImpl<Record> {...rest} />
    </Configurable>
  );
}
